import type { Strain, StrainQuery, StrainSummary } from '@shared';
import {
  getProductRepository,
  ProductRepositoryError,
  type ProductRepository,
} from '../productRepository';
import type { CatalogueBootstrapSource } from './bootstrapSource';
import { createServerCatalogueBootstrapSource } from './bootstrapSource';
import {
  classifyBootstrapSource,
  type BootstrapAvailability,
} from './identity';
import { mapServerStrainToLocalImport } from './mapSourceProduct';

export interface BootstrapCandidate {
  source: StrainSummary;
  status: BootstrapAvailability;
  localStrainId?: string;
  reason: string;
}

export type BootstrapImportOutcome =
  | 'imported'
  | 'skipped_already_imported'
  | 'conflict'
  | 'failed';

export interface BootstrapImportItemResult {
  sourceId: string;
  sourceLabel: string;
  outcome: BootstrapImportOutcome;
  localId?: string;
  error?: string;
}

export interface BootstrapImportBatchResult {
  results: BootstrapImportItemResult[];
  importedCount: number;
  skippedCount: number;
  conflictCount: number;
  failedCount: number;
}

export interface CatalogueBootstrapService {
  /** Load server catalogue candidates classified against the local destination. */
  listCandidates(
    query?: Partial<StrainQuery>,
  ): Promise<BootstrapCandidate[]>;
  /** Import selected server strain ids into the local repository. */
  importSelected(sourceIds: string[]): Promise<BootstrapImportBatchResult>;
  /** Import every currently available candidate (re-lists first). */
  importAllAvailable(
    query?: Partial<StrainQuery>,
  ): Promise<BootstrapImportBatchResult>;
}

export interface CatalogueBootstrapServiceOptions {
  source?: CatalogueBootstrapSource;
  /** Defaults to getProductRepository(); must remain local for imports. */
  getDestination?: () => ProductRepository;
}

function sourceLabel(source: Pick<StrainSummary, 'name' | 'productName' | 'productId' | 'id'>): string {
  return (
    source.name ??
    source.productName ??
    source.productId ??
    source.id
  );
}

async function loadLocalStrains(
  destination: ProductRepository,
): Promise<Strain[]> {
  const summaries = await destination.list({
    archived: 'all',
    sort: 'name',
  });
  return Promise.all(summaries.map((row) => destination.get(row.id)));
}

function requireLocalDestination(
  destination: ProductRepository,
): ProductRepository {
  if (destination.mode !== 'local') {
    throw new ProductRepositoryError(
      'Catalogue bootstrap import requires local product repository mode.',
      { code: 'invalid_mode', status: 400 },
    );
  }
  return destination;
}

export function createCatalogueBootstrapService(
  options: CatalogueBootstrapServiceOptions = {},
): CatalogueBootstrapService {
  const source = options.source ?? createServerCatalogueBootstrapSource();
  const getDestination = options.getDestination ?? getProductRepository;

  async function listCandidates(
    query: Partial<StrainQuery> = {
      archived: 'active',
      sort: 'name',
    },
  ): Promise<BootstrapCandidate[]> {
    const destination = getDestination();
    // Destination stays whatever is active (local in bootstrap UX).
    // Source reads are independent and do not switch the global repository.
    const [remote, locals] = await Promise.all([
      source.list(query),
      destination.mode === 'local'
        ? loadLocalStrains(destination)
        : Promise.resolve([] as Strain[]),
    ]);

    return remote.map((row) => {
      if (destination.mode !== 'local') {
        return {
          source: row,
          status: 'available' as const,
          reason: 'Bootstrap classification applies in local mode only.',
        };
      }
      const match = classifyBootstrapSource(row, locals);
      return {
        source: row,
        status: match.status,
        ...(match.localStrainId ? { localStrainId: match.localStrainId } : {}),
        reason: match.reason,
      };
    });
  }

  async function importOne(
    sourceId: string,
    destination: ProductRepository,
    locals: Strain[],
  ): Promise<{ result: BootstrapImportItemResult; locals: Strain[] }> {
    let remote: Strain;
    try {
      remote = await source.get(sourceId);
    } catch (err: unknown) {
      return {
        result: {
          sourceId,
          sourceLabel: sourceId,
          outcome: 'failed',
          error:
            err instanceof Error
              ? err.message
              : 'Failed to load source product from server catalogue.',
        },
        locals,
      };
    }

    const label = sourceLabel(remote);
    const match = classifyBootstrapSource(remote, locals);
    if (match.status === 'already_imported') {
      return {
        result: {
          sourceId,
          sourceLabel: label,
          outcome: 'skipped_already_imported',
          ...(match.localStrainId ? { localId: match.localStrainId } : {}),
        },
        locals,
      };
    }
    if (match.status === 'conflict') {
      return {
        result: {
          sourceId,
          sourceLabel: label,
          outcome: 'conflict',
          ...(match.localStrainId ? { localId: match.localStrainId } : {}),
          error: match.reason,
        },
        locals,
      };
    }

    try {
      const mapped = mapServerStrainToLocalImport(remote);
      const created = await destination.importFromCatalogueBootstrap(mapped);
      const nextLocals = [...locals, created];
      return {
        result: {
          sourceId,
          sourceLabel: label,
          outcome: 'imported',
          localId: created.id,
        },
        locals: nextLocals,
      };
    } catch (err: unknown) {
      return {
        result: {
          sourceId,
          sourceLabel: label,
          outcome: 'failed',
          error:
            err instanceof Error
              ? err.message
              : 'Failed to write imported product to local catalogue.',
        },
        locals,
      };
    }
  }

  async function importSelected(
    sourceIds: string[],
  ): Promise<BootstrapImportBatchResult> {
    const destination = requireLocalDestination(getDestination());
    let locals = await loadLocalStrains(destination);
    const results: BootstrapImportItemResult[] = [];

    // Sequential imports so duplicate protection sees prior writes in-batch.
    for (const sourceId of sourceIds) {
      const step = await importOne(sourceId, destination, locals);
      results.push(step.result);
      locals = step.locals;
    }

    return summarize(results);
  }

  async function importAllAvailable(
    query?: Partial<StrainQuery>,
  ): Promise<BootstrapImportBatchResult> {
    const candidates = await listCandidates(query);
    const availableIds = candidates
      .filter((c) => c.status === 'available')
      .map((c) => c.source.id);
    return importSelected(availableIds);
  }

  return { listCandidates, importSelected, importAllAvailable };
}

function summarize(
  results: BootstrapImportItemResult[],
): BootstrapImportBatchResult {
  return {
    results,
    importedCount: results.filter((r) => r.outcome === 'imported').length,
    skippedCount: results.filter(
      (r) => r.outcome === 'skipped_already_imported',
    ).length,
    conflictCount: results.filter((r) => r.outcome === 'conflict').length,
    failedCount: results.filter((r) => r.outcome === 'failed').length,
  };
}
